// src/app/shared/services/tealium-target.service.ts
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { ApiEndpoints } from '../../../config/api-endpoints';

declare global {
  interface Window {
    jQuery?: any;
    $?: any;
    utag_data?: Record<string, any>;
    upsNotifyViewChange?: (dataObject: { viewName: string; page?: boolean }) => void;
    trackPageViewOrAction?: (dataObject: any) => void;
  }
}

export interface RewardsLinkPayload {
  link_name: string;        // e.g., 'Redeem', 'Terms and Conditions Accept'
  content_block_id: string; // e.g., 'lnk_list', 'app_tsr', 'snd_teas', 'modal_tnc'
  link_page_id?: string;   // Defaults to 'rewards_account_dashboard'
}

@Injectable({
  providedIn: 'root'
})
export class TealiumTargetService {
  private renderer: Renderer2;
  private isLoaded = false;
  private isLoading = false;
  private readonly DEFAULT_PAGE_ID = 'rewards_account_dashboard';

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Dynamically loads utag.sync.js, jQuery, and utag.js only when NextGen loads
   */
  public initTealium(): Promise<void> {
    if (this.isLoaded) {
      return Promise.resolve();
    }
    if (this.isLoading) {
      return this.waitForTealiumReady();
    }

    this.isLoading = true;

    // 1. Declare UDO prior to utag.js loading
    window.utag_data = window.utag_data || {
      page_id: this.DEFAULT_PAGE_ID,
      page_name: 'Rewards Account Dashboard',
      site_area: 'Rewards_Dashboard',
      site_sub_area: 'Rewards_Dashboard'
    };

    // 2. Inject utag.sync.js (Adobe Target & Visitor API) into <head>
    const syncScript = this.renderer.createElement('script');
    syncScript.src = `//tags.tiqcdn.com/utag/ups/${ApiEndpoints.tealiumProfile}/${ApiEndpoints.tealiumEnv}/utag.sync.js`;
    syncScript.type = 'text/javascript';
    this.renderer.appendChild(document.head, syncScript);

    // 3. Ensure jQuery is available before loading utag.js container
    return this.loadJQueryIfNeeded().then(() => {
      return new Promise<void>((resolve) => {
        const utagScript = this.renderer.createElement('script');
        utagScript.src = `//tags.tiqcdn.com/utag/ups/${ApiEndpoints.tealiumProfile}/${ApiEndpoints.tealiumEnv}/utag.js`;
        utagScript.type = 'text/javascript';
        utagScript.async = true;

        utagScript.onload = () => {
          this.isLoaded = true;
          this.isLoading = false;
          resolve();
        };

        utagScript.onerror = (err: any) => {
          this.isLoading = false;
          console.error('[TealiumTargetService] Error loading utag.js', err);
          resolve(); // Resolve anyway so UI flow is never blocked
        };

        this.renderer.appendChild(document.head, utagScript);
      });
    });
  }

  /**
   * Loads jQuery dynamically only if not already present on window
   */
  // private loadJQueryIfNeeded(): Promise<void> {
  //   if (window.jQuery) {
  //     return Promise.resolve();
  //   }

  //   return new Promise((resolve) => {
  //     const jqScript = this.renderer.createElement('script');
  //     jqScript.src = 'https://code.jquery.com/jquery-3.7.1.min.js';
  //     jqScript.type = 'text/javascript';
  //     jqScript.onload = () => resolve();
  //     jqScript.onerror = () => {
  //       console.warn('[TealiumTargetService] jQuery failed to load; proceeding with utag.js');
  //       resolve();
  //     };
  //     this.renderer.appendChild(document.head, jqScript);
  //   });
  // }

  // In src/app/shared/services/tealium-target.service.ts

  /**
   * Loads the UPS vendor library containing jQuery and DOM analytics hooks
   */
  private loadJQueryIfNeeded(): Promise<void> {
    // If jQuery or $ is already exposed globally, proceed immediately
    if ((window as any).jQuery || (window as any).$) {
      return Promise.resolve();
    }

    // Provide a safe global fallback stub so utag.js extensions never throw ReferenceError
    (window as any).jQuery = (window as any).$ = (window as any).jQuery || function(selector: any) {
      if (typeof selector === 'function') {
        // Handle $(document).ready() or $(function() {})
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
          setTimeout(selector, 1);
        } else {
          document.addEventListener('DOMContentLoaded', selector);
        }
      }
      return {
        on: () => {},
        off: () => {},
        click: () => {},
        ready: (fn: any) => fn && fn(),
        find: () => ({ length: 0 }),
        length: 0
      };
    };

    return new Promise((resolve) => {
      const vendorScript = this.renderer.createElement('script');
      // Official UPS vendor library referenced in Section 5.2 of the guide
      vendorScript.src = 'https://www.ups.com/assets/resources/scripts/vendor/ups.vendor.7f56e3.js';
      vendorScript.type = 'text/javascript';
      
      vendorScript.onload = () => resolve();
      vendorScript.onerror = () => {
        // If external corporate script is blocked on localhost, fallback to standard CDN jQuery
        const cdnScript = this.renderer.createElement('script');
        cdnScript.src = 'https://code.jquery.com/jquery-3.7.1.min.js';
        cdnScript.type = 'text/javascript';
        cdnScript.onload = () => resolve();
        cdnScript.onerror = () => resolve();
        this.renderer.appendChild(document.head, cdnScript);
      };

      this.renderer.appendChild(document.head, vendorScript);
    });
  }

  /**
   * Tracks Page View for Target (upsNotifyViewChange) and Analytics (trackPageViewOrAction)
   */
  public trackRewardsPageView(pageName: string = 'Rewards Account Dashboard'): void {
    const pageData = {
      page_id: this.DEFAULT_PAGE_ID,
      page_name: pageName,
      site_area: 'Rewards_Dashboard',
      site_sub_area: 'Rewards_Dashboard'
    };

    // 1. Adobe Target View Change (VEC support)
    if (typeof window.upsNotifyViewChange === 'function') {
      window.upsNotifyViewChange({ viewName: this.DEFAULT_PAGE_ID, page: true });
    }

    // 2. Tealium / Analytics Tracking Call
    if (typeof window.trackPageViewOrAction === 'function') {
      window.trackPageViewOrAction({
        data: pageData,
        actionType: 'view',
        callback: () => {}
      });
    }
  }

  /**
   * Tracks Link clicks, CTA buttons, FAQs, and modal choices matching the Excel SDR
   */
  public trackRewardsLink(payload: RewardsLinkPayload): void {
    const linkData = {
      link_name: payload.link_name,
      content_block_id: payload.content_block_id,
      link_page_id: payload.link_page_id || this.DEFAULT_PAGE_ID
    };

    if (typeof window.trackPageViewOrAction === 'function') {
      window.trackPageViewOrAction({
        data: linkData,
        actionType: 'link',
        callback: () => {}
      });
    }
  }

  private waitForTealiumReady(): Promise<void> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (this.isLoaded) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);
    });
  }
}